import {
  HandshakeResponse,
  InternalWindow,
  MobileSDKAction,
  MobileSDKMessage,
  NativeHookMessage
} from './types'

interface MobileSDKMessagingParams {
  componentName: string
  componentVersion: string
}

/**
 * Class responsible for initializing bidirectional exchange of signals
 * between WebSDK and Mobile SDK to allow Native features such as fullscreeen view
 */
class MobileSDKMessaging {
  private readonly MSG_BRIDGE_SCRIPT_URL = 'https://x.klarnacdn.net/mobile-sdk/mobile-js-snippet/v1/app.min.js'

  private sender: string

  private receiver: string

  private componentVersion: string

  private pendingMessages: Map<
  string,
  { resolve: (value: unknown) => void; reject: (reason?: unknown) => void }
  >

  private handshakeResponse: HandshakeResponse | null = null

  constructor({ componentName, componentVersion }: MobileSDKMessagingParams) {
    this.sender = componentName
    this.componentVersion = componentVersion
    this.receiver = 'Native'
    this.pendingMessages = new Map()
  }

  /**
   * Checks if the library runs in a Mobile SDK context(e.g. WebView integrated with Mobile SDK).
   * @param win - The window object.
   */
  static isMobileSDKContext(
    { win }: { win: Window & InternalWindow } = {
      win: window,
    }
  ): boolean {
    if (
      win.webkit
      && win.webkit.messageHandlers
      && win.webkit.messageHandlers.KlarnaNativeHookMessageHandler
    ) {
      // If iOS' WKWebView
      return true
    }

    // If iOS' UIWebView or Android's WebView
    if (win.KlarnaNativeHookMessageHandler) {
      return true
    }
    return false
  }

  /**
   * Initializes handshake with Mobile SDK
   */
  async handshake() {
    await this.loadMessagingBridgeScript()

    this.addMessageReceiver()

    const response = await this.send<unknown, NativeHookMessage>({
      action: MobileSDKAction.HANDSHAKE,
      data: {
        componentName: this.sender,
        componentVersion: this.componentVersion,
      },
    })

    this.handshakeResponse = (response?.params as HandshakeResponse) ?? null

    return response
  }

  /**
   * Sends URL to Mobile SDK to open in a separate fullscreen
   * @param url safe page URL
   */
  loadSeparateFullscreenUrl(url: string) {
    return this.send({
      action: MobileSDKAction.LOAD_FULLSCREEN_URL,
      data: {
        url,
      },
    })
  }

  /**
   * Sends a signal to open separate fullscreen native view
   */
  showSeparateFullscreen() {
    this.send({
      action: MobileSDKAction.FULLSCREEN_SHOW,
      data: {
        placement: 'full',
      },
    })
  }

  /**
   * Sends a signal to open external browser
   * @param url safe page URL
   */
  openExternalBrowser(url: string) {
    this.send({
      action: MobileSDKAction.OPEN_EXTERNAL_BROWSER,
      data: {
        url,
      },
    })
  }

  /**
   * Sends a signal to open the sandboxed internal browser
   * @param url safe page URL
   */
  showSandboxedInternalBrowser(url: string) {
    this.send({
      action: MobileSDKAction.SHOW_SANDBOXED_INTERNAL_BROWSER,
      data: { url },
    })
  }

  /**
   * Whether the Mobile SDK advertised support for the sandboxed internal browser.
   */
  isSandboxedInternalBrowserSupported(): boolean {
    return this.handshakeResponse?.features?.includes('sandboxed-internal-browser') ?? false
  }

  /**
   * Sends a signal to hide separate fullscreen in native view
   */
  hideSeparateFullscreen() {
    this.send({
      action: MobileSDKAction.FULLSCREEN_HIDE,
      data: {},
    })
  }

  /**
   * Lazy loads Mobile SDK bridge script to enable signal exchange
   */
  private loadMessagingBridgeScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if the script is already loaded
      if (document.querySelector(`script[src="${this.MSG_BRIDGE_SCRIPT_URL}"]`) && window.__KlarnaNativeHook) {
        resolve()
        return
      }

      const script = document.createElement('script')
      script.src = this.MSG_BRIDGE_SCRIPT_URL

      script.onload = () => {
        if (!window.__KlarnaNativeHook) {
          reject(new Error(`Failed to load script: ${this.MSG_BRIDGE_SCRIPT_URL}`))
          return
        }

        resolve()
      }

      script.onerror = () => {
        reject(new Error(`Failed to load script: ${this.MSG_BRIDGE_SCRIPT_URL}`))
      }

      document.head.appendChild(script)
    })
  }

  /**
   * Enables WebSDK sender to receive signal responses from Mobile SDK
   */
  private addMessageReceiver() {
    window.__KlarnaNativeHook!.addReceiver(this.sender, (msg: NativeHookMessage) => {
      const { messageId } = msg

      if (this.pendingMessages.has(messageId)) {
        const { resolve, reject } = this.pendingMessages.get(messageId)!

        // error handling for specific signal responses
        if (
          [
            'fullscreenLoadUrlResponse',
            'fullscreenShowResponse',
            'fullscreenHideResponse',
          ].includes(msg.action)
        ) {
          const isSuccess = Boolean(msg.params['success'])

          if (isSuccess) {
            resolve(msg)
          } else {
            reject(new Error('Failed to perform action in Mobile SDK'))
          }
        } else {
          resolve(msg)
        }

        this.pendingMessages.delete(messageId)
      }
    })
  }

  /**
   * Sends a signal to Mobile SDK
   */
  private send<T, K>({ action, data }: MobileSDKMessage<T>): Promise<K> {
    return new Promise((resolve, reject) => {
      const messageId = this.getRandomMessageId()

      this.pendingMessages.set(messageId, { resolve, reject })

      window.__KlarnaNativeHook!.postMessage({
        action,
        messageId,
        sender: this.sender,
        receiver: this.receiver,
        params: { ...data },
      })
    })
  }

  private getRandomMessageId() {
    return Math.random().toString(36).substring(2, 9)
  }
}

export default MobileSDKMessaging
